import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Stack,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Euro as EuroIcon,
  Calculate as CalculateIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import api from '../services/api';

const AdminSalary = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [salaryCalculations, setSalaryCalculations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Salary details dialog
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  
  // SODRA rates for 2025
  const SODRA_RATES = {
    employee_pension: 0.195,  // 19.5% - Employee pension insurance
    employee_health: 0.0698,  // 6.98% - Employee health insurance  
    employee_unemployment: 0.0131, // 1.31% - Employee unemployment
    employer_social: 0.0177,  // 1.77% - Employer social insurance
    employer_guarantee: 0.0016, // 0.16% - Guarantee fund
    employer_accident: 0.0014, // 0.14% - Accident insurance
  };
  
  // Income tax calculation (2025 Lithuanian rates)
  const calculateIncomeTax = (grossSalary) => {
    const taxFreeAmount = 747; // 2025 tax-free amount
    const taxableIncome = Math.max(0, grossSalary - taxFreeAmount);
    const taxRate = 0.20; // 20% income tax rate
    return taxableIncome * taxRate;
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.filter(emp => emp.status === 'active'));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const calculateAllSalaries = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/salary/calculate-all', {
        month: selectedMonth,
        year: selectedYear
      });
      
      setSalaryCalculations(response.data.calculations || []);
      setSuccess(`Calculated salaries for ${response.data.calculations.length} employees`);
    } catch (error) {
      setError('Failed to calculate salaries');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateIndividualSalary = async (employeeId) => {
    try {
      const response = await api.post('/salary/calculate', {
        employee_id: employeeId,
        month: selectedMonth,
        year: selectedYear
      });
      
      setSelectedSalary(response.data);
      setOpenDetailsDialog(true);
    } catch (error) {
      setError('Failed to calculate salary');
    }
  };

const generatePayslip = async (employeeId, salaryData) => {
  try {
    const response = await api.post('/payslips/generate', {
      employee_id: employeeId,
      month: selectedMonth,
      year: selectedYear,
      ...salaryData
    });
    
    setSuccess('Payslip generated successfully');
    
    // Refresh the calculations to get the payslip IDs
    await calculateAllSalaries();
    
  } catch (error) {
    setError('Failed to generate payslip');
  }
};

const downloadPayslip = async (payslipId, employeeName) => {
  try {
    const response = await api.get(`/payslips/${payslipId}/download`, {
      responseType: 'blob'  // Important for binary data
    });
    
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `payslip_${selectedMonth}_${selectedYear}_${employeeName.replace(/\s+/g, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    setSuccess('Payslip downloaded successfully');
  } catch (error) {
    setError('Failed to download payslip');
    console.error('Download error:', error);
  }
};


  const generateAllPayslips = async () => {
    setLoading(true);
    try {
      let generatedCount = 0;
      
      for (const calc of salaryCalculations) {
        if (!calc.payslip_generated) {
          await generatePayslip(calc.employee_id, calc);
          generatedCount++;
        }
      }
      
      setSuccess(`Generated ${generatedCount} payslips successfully`);
    } catch (error) {
      setError('Failed to generate all payslips');
    } finally {
      setLoading(false);
    }
  };

  

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('lt-LT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getMonthName = (month) => {
    return new Date(2000, month - 1).toLocaleDateString('en-US', { month: 'long' });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Salary Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Calculate salaries and generate payslips for employees
        </Typography>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Period Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Period
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                label="Month"
              >
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {getMonthName(i + 1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Year"
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={calculateAllSalaries}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              Calculate All Salaries
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              onClick={generateAllPayslips}
              disabled={loading || salaryCalculations.length === 0}
            >
              Generate All Payslips
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* SODRA Rates Info */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Employee Contributions
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Pension Insurance:</Typography>
                  <Typography variant="body2" fontWeight="bold">19.5%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Health Insurance:</Typography>
                  <Typography variant="body2" fontWeight="bold">6.98%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Unemployment:</Typography>
                  <Typography variant="body2" fontWeight="bold">1.31%</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">Total Employee:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="error">27.79%</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Employer Contributions
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Social Insurance:</Typography>
                  <Typography variant="body2" fontWeight="bold">1.77%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Guarantee Fund:</Typography>
                  <Typography variant="body2" fontWeight="bold">0.16%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Accident Insurance:</Typography>
                  <Typography variant="body2" fontWeight="bold">0.14%</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">Total Employer:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">2.07%</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Salary Calculations Table */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      {salaryCalculations.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Salary Calculations - {getMonthName(selectedMonth)} {selectedYear}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell align="right">Hours Worked</TableCell>
                  <TableCell align="right">Gross Salary</TableCell>
                  <TableCell align="right">SODRA (Employee)</TableCell>
                  <TableCell align="right">Income Tax</TableCell>
                  <TableCell align="right">Net Salary</TableCell>
                  <TableCell align="right">SODRA (Employer)</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salaryCalculations.map((calc) => (
                  <TableRow key={calc.employee_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {calc.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {calc.personal_code}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {calc.total_hours}h
                      {calc.overtime_hours > 0 && (
                        <Chip 
                          label={`+${calc.overtime_hours}h OT`} 
                          size="small" 
                          color="secondary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(calc.gross_salary)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      -{formatCurrency(calc.sodra_employee_total)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      -{formatCurrency(calc.income_tax)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary">
                        {formatCurrency(calc.net_salary)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(calc.sodra_employer_total)}
                    </TableCell>
                    <TableCell align="center">
                      {calc.payslip_generated ? (
                        <Chip 
                          icon={<CheckIcon />} 
                          label="Generated" 
                          color="success" 
                          size="small" 
                        />
                      ) : (
                        <Chip 
                          icon={<WarningIcon />} 
                          label="Pending" 
                          color="warning" 
                          size="small" 
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setSelectedSalary(calc);
                              setOpenDetailsDialog(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {!calc.payslip_generated && (
                          <Tooltip title="Generate Payslip">
                            <IconButton 
                              size="small"
                              color="primary"
                              onClick={() => generatePayslip(calc.employee_id, calc)}
                            >
                              <ReceiptIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {calc.payslip_generated && (
                          <Tooltip title="Download Payslip">
                            <IconButton size="small" color="success">
				{calc.payslip_generated && calc.payslip_id && (
  <Tooltip title="Download Payslip">
    <IconButton 
      size="small" 
      color="success"
      onClick={() => downloadPayslip(calc.payslip_id, calc.employee_name)}
    >
      <DownloadIcon />
    </IconButton>
  </Tooltip>
)}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Totals */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total Gross</Typography>
                <Typography variant="h6">
                  {formatCurrency(salaryCalculations.reduce((sum, calc) => sum + calc.gross_salary, 0))}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total Net</Typography>
                <Typography variant="h6">
                  {formatCurrency(salaryCalculations.reduce((sum, calc) => sum + calc.net_salary, 0))}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total SODRA (Employee)</Typography>
                <Typography variant="h6">
                  {formatCurrency(salaryCalculations.reduce((sum, calc) => sum + calc.sodra_employee_total, 0))}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total SODRA (Employer)</Typography>
                <Typography variant="h6">
                  {formatCurrency(salaryCalculations.reduce((sum, calc) => sum + calc.sodra_employer_total, 0))}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Salary Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Salary Details - {selectedSalary?.employee_name}
        </DialogTitle>
        <DialogContent>
          {selectedSalary && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Earnings */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Earnings</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Regular Hours</TableCell>
                        <TableCell align="right">{selectedSalary.regular_hours}h</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Hourly Rate</TableCell>
                        <TableCell align="right">{formatCurrency(selectedSalary.hourly_rate)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Base Salary</TableCell>
                        <TableCell align="right">{formatCurrency(selectedSalary.base_salary)}</TableCell>
                      </TableRow>
                      {selectedSalary.overtime_hours > 0 && (
                        <>
                          <TableRow>
                            <TableCell>Overtime Hours</TableCell>
                            <TableCell align="right">{selectedSalary.overtime_hours}h</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Overtime Pay</TableCell>
                            <TableCell align="right">{formatCurrency(selectedSalary.overtime_pay)}</TableCell>
                          </TableRow>
                        </>
                      )}
                      <TableRow>
                        <TableCell><strong>Gross Salary</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(selectedSalary.gross_salary)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              
              {/* Deductions */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Deductions</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Pension (19.5%)</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          -{formatCurrency(selectedSalary.sodra_pension)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Health (6.98%)</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          -{formatCurrency(selectedSalary.sodra_health)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Unemployment (1.31%)</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          -{formatCurrency(selectedSalary.sodra_unemployment)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Income Tax (20%)</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          -{formatCurrency(selectedSalary.income_tax)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Total Deductions</strong></TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          <strong>-{formatCurrency(selectedSalary.total_deductions)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              
              {/* Net Salary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                  <Typography variant="h6" gutterBottom>Net Salary</Typography>
                  <Typography variant="h3">
                    {formatCurrency(selectedSalary.net_salary)}
                  </Typography>
                </Paper>
              </Grid>
              
              {/* Employer Costs */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Employer Contributions</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Social Insurance (1.77%)</TableCell>
                        <TableCell align="right">{formatCurrency(selectedSalary.employer_social)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Guarantee Fund (0.16%)</TableCell>
                        <TableCell align="right">{formatCurrency(selectedSalary.employer_guarantee)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Accident Insurance (0.14%)</TableCell>
                        <TableCell align="right">{formatCurrency(selectedSalary.employer_accident)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Total Employer Cost</strong></TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(selectedSalary.total_employer_cost)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
          {selectedSalary && !selectedSalary.payslip_generated && (
            <Button 
              variant="contained" 
              startIcon={<ReceiptIcon />}
              onClick={() => {
                generatePayslip(selectedSalary.employee_id, selectedSalary);
                setOpenDetailsDialog(false);
              }}
            >
              Generate Payslip
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminSalary;
